import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      
      // Fetch detailed subscription info
      try {
        const subResponse = await axios.get(`${API}/subscription/info`);
        setSubscriptionInfo({
          can_access: response.data.can_access,
          status: subResponse.data.status,
          trial_days_left: response.data.trial_days_left,
          subscription_start_date: subResponse.data.subscription_start_date,
          subscription_end_date: subResponse.data.subscription_end_date,
          next_billing_date: subResponse.data.next_billing_date,
          cancel_at_period_end: subResponse.data.cancel_at_period_end
        });
      } catch (subError) {
        // Fallback to basic info if subscription endpoint fails
        console.error('Failed to fetch subscription info:', subError);
        setSubscriptionInfo({
          can_access: response.data.can_access,
          status: response.data.status,
          trial_days_left: response.data.trial_days_left
        });
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const signup = async (email, password) => {
    const response = await axios.post(`${API}/auth/signup`, { email, password });
    const { access_token, user } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setUser(user);
    return user;
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setUser(user);
    await fetchUser();
    return user;
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setSubscriptionInfo(null);
    // Redirect to landing page
    window.location.href = '/';
  };

  const refreshUser = async () => {
    await fetchUser();
  };

  return (
    <AuthContext.Provider value={{ user, loading, subscriptionInfo, signup, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};
